import { requirePermission } from "@/lib/casl/route-guards";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useGetAllEscalations } from "@/lib/api/escalations";
import { findMentorship } from "@/lib/api/mentorships";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getMenteeDisplayName,
  isFieldRedacted,
} from "@/lib/redacted-display";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/escalations/")({
  component: EscalationsPage,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "escalations");
  },
});

function EscalationCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-6 w-16" />
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div>
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    </Card>
  );
}

export default function EscalationsPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGetAllEscalations(page);
  const navigate = useNavigate();

  const handleEscalationClick = async (mentorId: string, menteeId: string) => {
    try {
      const mentorship = await findMentorship(mentorId, menteeId);
      if (mentorship) {
        navigate({ to: "/mentorships/$mentorshipId", params: { mentorshipId: mentorship.id } });
        return;
      }
    } catch (err) {
      console.error("Error finding mentorship:", err);
    }
    navigate({ to: "/users/$userId", params: { userId: menteeId } });
  };

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Escalations</h1>
        <p className="text-muted-foreground">
          Manage and respond to ongoing situations
        </p>
      </div>

      <div className="space-y-4">
        {isLoading
          ? [...Array(3)].map((_, i) => <EscalationCardSkeleton key={i} />)
          : data?.escalations.map((escalation) => {
              const menteeRedactedFields = escalation.mentee.redacted_fields;
              const menteeDisplayName = getMenteeDisplayName(escalation.mentee);
                // Hide redacted emergency contacts because "[redacted]" is not actionable here.
              const showGuardian =
                !isFieldRedacted("guardian_name", menteeRedactedFields) &&
                !!escalation.mentee?.guardian_name;
              const showEmergencyPhone =
                !isFieldRedacted("emergency_phone", menteeRedactedFields) &&
                !!escalation.mentee?.emergency_phone;
              return (
              <div
                key={escalation.id}
                onClick={() => handleEscalationClick(escalation.mentor.id, escalation.mentee.id)}
              >
                <Card
                  className={cn([
                    escalation.status === "OPEN" ? "bg-red-50" : "bg-green-50",
                    "cursor-pointer",
                  ])}
                >
                  <CardContent className="p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold">
                            {menteeDisplayName}
                          </h2>
                          {escalation.status === "OPEN" && (
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Escalated by{" "}
                          {escalation.started_by?.name || "System"} on{" "}
                          {format(
                            new Date(escalation.created_at),
                            "MMM d, yyyy 'at' h:mm a",
                          )}
                        </div>
                      </div>
                      <Badge
                        variant={
                          escalation.status === "OPEN"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {escalation.status === "OPEN" ? "Active" : "Resolved"}
                      </Badge>
                    </div>

                    <div className="mb-4 grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium">
                          Assigned Mentor
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {escalation.mentor.name}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium">
                          Emergency Contact
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {showGuardian && escalation.mentee.guardian_name}
                          {showEmergencyPhone && (
                            <>
                              {showGuardian && <br />}
                              {escalation.mentee.emergency_phone}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {escalation.reason && (
                      <div className="mb-4">
                        <div className="text-sm font-medium">
                          Reason for Escalation
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {escalation.reason}
                        </div>
                      </div>
                    )}

                    {escalation.metadata && (
                      <div className="mt-4 border-t pt-4">
                        <h3 className="mb-2 text-lg font-semibold">
                          Escalation Details
                        </h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="font-medium">Escalation Type</div>
                            <div className="text-muted-foreground">
                              {escalation.metadata.escalationType}
                            </div>
                          </div>
                          {escalation.metadata.crisisSubType && (
                            <div>
                              <div className="font-medium">Crisis Sub-type</div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.crisisSubType}
                              </div>
                            </div>
                          )}
                          <div>
                            <div className="font-medium">Referral?</div>
                            <div className="text-muted-foreground">
                              {escalation.metadata.wasReferral ? "Yes" : "No"}
                            </div>
                          </div>
                          {escalation.metadata.referralType && (
                            <div>
                              <div className="font-medium">Referral Type</div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.referralType}
                              </div>
                            </div>
                          )}
                          {escalation.metadata.reasonForEscalation && (
                            <div className="col-span-2">
                              <div className="font-medium">
                                Reason for Escalation
                              </div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.reasonForEscalation}
                              </div>
                            </div>
                          )}
                          {escalation.metadata.explainReasonForAlert && (
                            <div className="col-span-2">
                              <div className="font-medium">
                                Reason for Alert
                              </div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.explainReasonForAlert}
                              </div>
                            </div>
                          )}
                          {escalation.metadata.suicideAssessmentRisk && (
                            <div className="col-span-2">
                              <div className="font-medium">
                                Suicide Assessment Risk
                              </div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.suicideAssessmentRisk}
                              </div>
                            </div>
                          )}
                          {escalation.metadata.informationProvidedByMentor && (
                            <div className="col-span-2">
                              <div className="font-medium">
                                Information Provided by Mentor
                              </div>
                              <div className="text-muted-foreground">
                                {
                                  escalation.metadata
                                    .informationProvidedByMentor
                                }
                              </div>
                            </div>
                          )}
                          {escalation.metadata
                            .conversationsWithEmergencyContact && (
                            <div className="col-span-2">
                              <div className="font-medium">
                                Conversations with Emergency Contact/First
                                Responders
                              </div>
                              <div className="text-muted-foreground">
                                {
                                  escalation.metadata
                                    .conversationsWithEmergencyContact
                                }
                              </div>
                            </div>
                          )}
                          <div className="col-span-2">
                            <div className="font-medium">Next Steps</div>
                            <div className="text-muted-foreground">
                              {escalation.metadata.nextSteps}
                            </div>
                          </div>
                          {escalation.metadata.safetyPlan && (
                            <div className="col-span-2">
                              <div className="font-medium">Safety Plan</div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.safetyPlan}
                              </div>
                            </div>
                          )}
                          {escalation.metadata.additionalNotes && (
                            <div className="col-span-2">
                              <div className="font-medium">
                                Additional Notes
                              </div>
                              <div className="text-muted-foreground">
                                {escalation.metadata.additionalNotes}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {escalation.status === "CLOSED" && (
                      <div className="mt-4 border-t pt-4 text-sm text-muted-foreground">
                        <div>
                          Resolved by {escalation.closed_by?.name} on{" "}
                          {format(
                            new Date(escalation.closed_at!),
                            "MMM d, yyyy 'at' h:mm a",
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              );
            })}
      </div>

      {data && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Page {data.page}</div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.is_last_page}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
